import { API_BASE_URL } from './api';

const EMPLOYEE_TOKEN_KEY = 'employeeAuthToken';
const EMPLOYEE_PROFILE_KEY = 'employeeProfile';
const EMPLOYEE_REGISTRATION_DRAFT_KEY = 'employeeRegistrationDraft';

export const getEmployeeToken = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return localStorage.getItem(EMPLOYEE_TOKEN_KEY);
};

export const getStoredEmployeeProfile = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = localStorage.getItem(EMPLOYEE_PROFILE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

export const setEmployeeSession = (token, profile = null) => {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.setItem(EMPLOYEE_TOKEN_KEY, token);
  if (profile) {
    localStorage.setItem(EMPLOYEE_PROFILE_KEY, JSON.stringify(profile));
  } else {
    localStorage.removeItem(EMPLOYEE_PROFILE_KEY);
  }
};

export const updateStoredEmployeeProfile = (profile) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!profile) {
    localStorage.removeItem(EMPLOYEE_PROFILE_KEY);
    return;
  }

  localStorage.setItem(EMPLOYEE_PROFILE_KEY, JSON.stringify(profile));
};

export const clearEmployeeSession = (navigate) => {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.removeItem(EMPLOYEE_TOKEN_KEY);
  localStorage.removeItem(EMPLOYEE_PROFILE_KEY);

  if (navigate) {
    navigate('/employee/mobile/login', { replace: true });
    return;
  }

  window.location.replace('/employee/mobile/login');
};

export const employeeApiRequest = async (path, options = {}) => {
  const {
    method = 'GET',
    body,
    headers = {},
  } = options;

  const token = getEmployeeToken();
  const finalHeaders = { ...headers };
  if (token) {
    finalHeaders.Authorization = `Bearer ${token}`;
  }

  let requestBody = body;
  if (body && !(body instanceof FormData)) {
    requestBody = JSON.stringify(body);
    if (!finalHeaders['Content-Type']) {
      finalHeaders['Content-Type'] = 'application/json';
    }
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: finalHeaders,
    body: requestBody,
  });

  let result = null;
  try {
    result = await response.json();
  } catch (error) {
    result = null;
  }

  if (response.status === 401) {
    const normalizedPath = typeof path === 'string' ? path.split('?')[0] : '';
    const isUnauthenticatedPublicFlow =
      normalizedPath.startsWith('/api/public/employee/login') ||
      normalizedPath.startsWith('/api/public/employee/register') ||
      normalizedPath.startsWith('/api/public/employee/registration/status') ||
      normalizedPath.startsWith('/api/public/documents/upload') ||
      normalizedPath.startsWith('/api/public/education-qualifications/upload');
    const isProtectedEmployeeEndpoint =
      normalizedPath.startsWith('/api/public/employee/me') ||
      normalizedPath.startsWith('/api/public/employee/payslips') ||
      normalizedPath.startsWith('/api/public/employee/notifications') ||
      normalizedPath.startsWith('/api/public/employee/enquiries');
    const shouldClear = Boolean(token) && isProtectedEmployeeEndpoint && !isUnauthenticatedPublicFlow;
    if (shouldClear) {
      clearEmployeeSession();
    }
    throw new Error(result?.message || 'Your session has expired. Please login again.');
  }

  if (!response.ok || result?.status !== 'SUCCESS') {
    throw new Error(result?.message || 'Failed to process request.');
  }

  return {
    data: result?.data ?? null,
    message: result?.message ?? '',
    meta: result?.meta ?? null,
  };
};

export default {
  employeeApiRequest,
  getEmployeeToken,
  setEmployeeSession,
  clearEmployeeSession,
  getStoredEmployeeProfile,
  updateStoredEmployeeProfile,
};

export const getEmployeeRegistrationDraft = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(EMPLOYEE_REGISTRATION_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
};

export const setEmployeeRegistrationDraft = (draft) => {
  if (typeof window === 'undefined') return;
  if (!draft) {
    localStorage.removeItem(EMPLOYEE_REGISTRATION_DRAFT_KEY);
    return;
  }
  localStorage.setItem(EMPLOYEE_REGISTRATION_DRAFT_KEY, JSON.stringify(draft));
};

export const clearEmployeeRegistrationDraft = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(EMPLOYEE_REGISTRATION_DRAFT_KEY);
};
